import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import styles from './FieldRow.module.scss';

/** С какой ширины поля встают в строку; уже — идут друг под другом. */
export type FieldRowFrom = 'sm' | 'md';

const FieldRowContext = createContext<FieldRowFrom | null>(null);

/** Поле внутри FieldRow (и с какой ширины): FormField укладывает подпись в одну линию. */
export function useFieldRow(): FieldRowFrom | null {
  return useContext(FieldRowContext);
}

export interface FieldRowProps {
  from?: FieldRowFrom;
  children: ReactNode;
}

/**
 * Поля в строку (SYS-UC-12.CC-2). Выравнивание по верху: подписи в одну линию, поэтому поля ввода стоят
 * ровно, а подсказка или ошибка под одним полем не сдвигает соседние. Растягивающееся поле —
 * FieldRowGrow, кнопки — FieldRowActions.
 */
export function FieldRow({ from = 'md', children }: FieldRowProps): ReactElement {
  return (
    <FieldRowContext.Provider value={from}>
      <div className={`${styles.row} ${from === 'sm' ? styles.fromSm : styles.fromMd}`}>{children}</div>
    </FieldRowContext.Provider>
  );
}

/** Поле, которое забирает свободную ширину строки. */
export function FieldRowGrow({ children }: { children: ReactNode }): ReactElement {
  return <div className={styles.grow}>{children}</div>;
}

export interface FieldRowActionsProps {
  /** Прижать к правому краю строки. */
  end?: boolean;
  children: ReactNode;
}

/** Кнопки строки — на линии полей ввода: сверху пустая строка высотой с подпись поля. */
export function FieldRowActions({ end, children }: FieldRowActionsProps): ReactElement {
  return (
    <div className={`${styles.actions} ${end ? styles.end : ''}`}>
      <span className={styles.labelSpace} aria-hidden="true">
        &nbsp;
      </span>
      <div className={styles.buttons}>{children}</div>
    </div>
  );
}
